<template>
  <div class="jobs-page">
    <!-- Header -->
    <div class="jobs-hero">
      <div class="jobs-container jobs-hero-inner">
        <div>
          <p class="jobs-kicker">Operations</p>
          <h1 class="jobs-title">System Dashboard</h1>
          <p class="jobs-subtitle">Job queue status, worker health, and site runtime state</p>
        </div>
        <button
          @click="refresh"
          class="jobs-refresh"
          :disabled="loading"
        >
          <svg v-if="!loading" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <svg v-else class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Refresh
        </button>
      </div>
    </div>

    <div class="jobs-container jobs-content">
      <!-- Job queue backlog summary -->
      <section class="jobs-section">
        <h2 class="jobs-section-title">Job Queue</h2>
        <div class="jobs-grid">
          <div
            v-for="(count, status) in backlog"
            :key="status"
            class="jobs-card"
          >
            <span :class="['jobs-pill', jobStatusClass(status)]">{{ status }}</span>
            <p class="jobs-card-value">{{ count }}</p>
          </div>
        </div>
      </section>

      <!-- Worker health -->
      <section class="jobs-section">
        <h2 class="jobs-section-title">Workers</h2>
        <div v-if="workers.length === 0" class="jobs-empty">No workers registered.</div>
        <div v-else class="jobs-stack">
          <div
            v-for="w in workers"
            :key="w.id"
            class="jobs-card jobs-row"
          >
            <div>
              <p class="jobs-row-title">{{ w.id }}</p>
              <p class="jobs-row-meta">
                Active jobs: {{ w.activeJobCount }} &middot;
                Last heartbeat: {{ heartbeatLabel(w.heartbeatAgeSeconds) }}
              </p>
            </div>
            <span :class="['jobs-pill', workerStatusClass(w.status)]">
              {{ w.status }}
            </span>
          </div>
        </div>
      </section>

      <!-- Recent jobs -->
      <section class="jobs-section">
        <div class="jobs-section-head">
          <h2 class="jobs-section-title">Recent Jobs</h2>
          <select
            v-model="jobStatusFilter"
            @change="loadJobs"
            class="jobs-select"
          >
            <option value="">All statuses</option>
            <option value="queued">Queued</option>
            <option value="running">Running</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        <div v-if="jobs.length === 0" class="jobs-empty">No jobs found.</div>
        <div v-else class="jobs-stack">
          <div
            v-for="job in jobs"
            :key="job.id"
            class="jobs-card"
          >
            <div class="jobs-row">
              <div class="jobs-row-main">
                <div class="jobs-row-tags">
                  <span :class="['jobs-pill', jobStatusClass(job.status)]">
                    {{ job.status }}
                  </span>
                  <span class="jobs-mono jobs-mono-muted">{{ job.jobType }}</span>
                </div>
                <p class="jobs-mono jobs-row-id">{{ job.id }}</p>
                <p v-if="job.updatePrompt" class="jobs-row-prompt">{{ job.updatePrompt }}</p>
                <p v-if="job.lastError" class="jobs-row-error">Error: {{ job.lastError }}</p>
              </div>
              <div class="jobs-row-side">
                <p>Attempt {{ job.attemptCount }}/{{ job.maxAttempts }}</p>
                <p>{{ formatDate(job.createdAt) }}</p>
                <p v-if="job.workerId" class="jobs-mono jobs-mono-muted jobs-row-worker">{{ shortWorker(job.workerId) }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Runtime summary -->
      <section class="jobs-section">
        <h2 class="jobs-section-title">Site Runtimes</h2>
        <div v-if="runtimeSummary" class="jobs-grid">
          <div class="jobs-card">
            <span class="jobs-pill jobs-pill-success">running</span>
            <p class="jobs-card-value">{{ runtimeSummary.running || 0 }}</p>
          </div>
          <div class="jobs-card">
            <span class="jobs-pill jobs-pill-neutral">stopped</span>
            <p class="jobs-card-value">{{ runtimeSummary.stopped || 0 }}</p>
          </div>
          <div class="jobs-card">
            <span class="jobs-pill jobs-pill-warning">transitioning</span>
            <p class="jobs-card-value">{{ runtimeSummary.transitioning || 0 }}</p>
          </div>
          <div class="jobs-card">
            <span class="jobs-pill jobs-pill-danger">unhealthy</span>
            <p class="jobs-card-value">{{ runtimeSummary.unhealthy || 0 }}</p>
          </div>
        </div>
        <div v-else class="jobs-empty">No runtime data available.</div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import api from '../utils/api'

const loading = ref(false)
const workers = ref([])
const backlog = ref({})
const jobs = ref([])
const jobStatusFilter = ref('')
const runtimeSummary = ref(null)

async function loadWorkerHealth() {
  const { data } = await api.get('/bwai/workers/health')
  workers.value = data.workers || []
  backlog.value = data.backlog || {}
}

async function loadJobs() {
  const params = { limit: 50 }
  if (jobStatusFilter.value) params.status = jobStatusFilter.value
  const { data } = await api.get('/bwai/jobs', { params })
  jobs.value = data.jobs || []
}

async function loadRuntimeSummary() {
  const { data } = await api.get('/bwai/runtimes')
  runtimeSummary.value = data.summary || null
}

async function refresh() {
  loading.value = true
  try {
    await Promise.all([loadWorkerHealth(), loadJobs(), loadRuntimeSummary()])
  } finally {
    loading.value = false
  }
}

onMounted(refresh)

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
}

function heartbeatLabel(ageSeconds) {
  if (ageSeconds == null) return 'unknown'
  if (ageSeconds < 60) return `${Math.round(ageSeconds)}s ago`
  return `${Math.round(ageSeconds / 60)}m ago`
}

function shortWorker(id) {
  const parts = String(id).split('-')
  return parts.length > 2 ? parts.slice(-2).join('-') : id
}

function jobStatusClass(status) {
  return {
    queued:    'jobs-pill-info',
    running:   'jobs-pill-warning',
    succeeded: 'jobs-pill-success',
    failed:    'jobs-pill-danger',
  }[status] || 'jobs-pill-neutral'
}

function workerStatusClass(status) {
  return {
    healthy:  'jobs-pill-success',
    degraded: 'jobs-pill-warning',
    down:     'jobs-pill-danger',
  }[status] || 'jobs-pill-neutral'
}
</script>

<style scoped>
.jobs-page {
  min-height: 100vh;
  color: var(--text-primary);
  background:
    radial-gradient(circle at 12% 0%, rgba(124,109,250,0.16), transparent 32rem),
    radial-gradient(circle at 86% 10%, rgba(56,189,248,0.10), transparent 28rem),
    var(--surface-base);
}
.jobs-container {
  width: min(1200px, calc(100% - 32px));
  margin: 0 auto;
}

.jobs-hero {
  border-bottom: var(--glass-border);
  background: rgba(15,15,24,0.72);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
}
.jobs-hero-inner {
  padding: 28px 0 24px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.jobs-kicker {
  margin: 0 0 6px;
  color: var(--accent);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}
.jobs-title {
  margin: 0;
  color: var(--text-primary);
  font-size: clamp(26px, 3.6vw, 36px);
  font-weight: 700;
  letter-spacing: -0.03em;
}
.jobs-subtitle {
  margin: 6px 0 0;
  color: var(--text-secondary);
  font-size: 13px;
}

.jobs-refresh {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: var(--accent-soft);
  border: 1px solid rgba(124,109,250,0.3);
  color: var(--accent);
  transition: all 0.15s;
  font-family: inherit;
}
.jobs-refresh:hover:not(:disabled) {
  background: rgba(124,109,250,0.20);
  border-color: rgba(124,109,250,0.5);
  color: var(--text-primary);
}
.jobs-refresh:disabled { opacity: 0.5; cursor: not-allowed; }

.jobs-content {
  padding: 28px 0 48px;
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.jobs-section { display: flex; flex-direction: column; gap: 14px; }
.jobs-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.jobs-section-title {
  margin: 0;
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.jobs-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
@media (min-width: 640px) {
  .jobs-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}

.jobs-card {
  padding: 16px;
  border-radius: var(--radius-md);
  background: var(--glass-bg);
  border: var(--glass-border);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  box-shadow: var(--elev-sm);
}
.jobs-card:hover { background: var(--glass-bg-hover); border: var(--glass-border-hi); }
.jobs-card-value {
  margin: 10px 0 0;
  color: var(--text-primary);
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.jobs-stack { display: flex; flex-direction: column; gap: 10px; }

.jobs-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
@media (min-width: 640px) {
  .jobs-row { flex-direction: row; align-items: flex-start; justify-content: space-between; }
}
.jobs-row-main { flex: 1; min-width: 0; }
.jobs-row-title { margin: 0; color: var(--text-primary); font-size: 13px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.jobs-row-meta  { margin: 4px 0 0; color: var(--text-muted); font-size: 11px; }
.jobs-row-tags  { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.jobs-row-id    { margin: 4px 0 0; color: var(--text-muted); font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.jobs-row-prompt{ margin: 6px 0 0; color: var(--text-secondary); font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.jobs-row-error { margin: 6px 0 0; color: #fca5a5; font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.jobs-row-side  {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;
  font-size: 11px;
  color: var(--text-muted);
}
@media (min-width: 640px) { .jobs-row-side { text-align: right; } }
.jobs-row-side p { margin: 0; }
.jobs-row-worker { max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.jobs-mono { font-family: ui-monospace, SFMono-Regular, monospace; font-size: 11px; }
.jobs-mono-muted { color: var(--text-muted); }

.jobs-empty {
  padding: 20px;
  border-radius: var(--radius-md);
  background: var(--glass-bg);
  border: var(--glass-border);
  color: var(--text-muted);
  font-size: 13px;
  text-align: center;
}

.jobs-select {
  background: var(--glass-bg-active);
  border: var(--glass-border);
  color: var(--text-primary);
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-family: inherit;
  outline: none;
}
.jobs-select:focus { border: var(--glass-border-hi); }

.jobs-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: 1px solid transparent;
}
.jobs-pill-info     { background: rgba(56,189,248,0.14); color: #93c5fd; border-color: rgba(56,189,248,0.3); }
.jobs-pill-success  { background: rgba(34,197,94,0.14);  color: #86efac; border-color: rgba(34,197,94,0.3); }
.jobs-pill-warning  { background: rgba(245,158,11,0.14); color: #fde68a; border-color: rgba(245,158,11,0.3); }
.jobs-pill-danger   { background: rgba(239,68,68,0.14);  color: #fca5a5; border-color: rgba(239,68,68,0.3); }
.jobs-pill-neutral  { background: var(--glass-bg-active); color: var(--text-muted); border-color: rgba(255,255,255,0.10); }
</style>
